/**
 * ROUND8: the tracker dog (docs/milestones/ROUND8-dogs.md, ROUND8-contracts.md §1) - a nose, not a gun. It never
 * bites and never raises the alert by itself: it finds him and says so, and its handler is the gun.
 *
 * `Enemy.dog.mode`, which the renderer and the sound read:
 *  - heel:  a pace off its handler's left shoulder on his beat (at its post, with no handler), nose working.
 *  - cast:  nose down, sweeping round where it lost the line - or round its handler while he searches.
 *  - track: running his trail print by print toward the newest end, at `DOGS.trackSpeed` (./dogTrail.ts).
 *  - bay:   it has his scent (./dogNose.ts). It holds `DOGS.bayDistance` off, follows as he moves and gives tongue
 *           every `DOGS.barkEverySeconds` - a shout, which draws whoever hears it; and its handler, wherever he is.
 *  - mourn: its handler is down. It stays by him, barking, until it is killed.
 *
 * A living dog sits in `state: 'down'` as the machines do (./index.ts): the one state every piece of infantry code
 * leaves alone, so it hears no footsteps (it smells), answers no shout, rescues nobody, is sent nowhere, takes no
 * prisoner and is nobody's squad-mate. Dead, it is a body like any other, and a man who finds it raises alert 1.
 */

import { bark, makeNoise, raiseAlert, sendToInvestigate } from '../../alert';
import { type Ctx, isFighter } from '../../ctx';
import { clamp, cos, sin, TAU } from '../../math';
import { waterAt } from '../../terrainQueries';
import { secs } from '../../tuning';
import { DOGS } from '../../tuningDogs';
import type { DogState, Enemy, World } from '../../types';
import { cellFree } from '../movement';
import { go, heelSpot, standFacing } from './dogMove';
import { smellsRanger } from './dogNose';
import { freshDogState } from './dogState';
import { freshPrint, printIndex, printNear } from './dogTrail';

/** Metres from a print at which it counts as reached and the dog makes for the next. */
const PRINT_REACH = 0.8;
/**
 * A man this near its dead handler, metres, sees what the dog is barking over: sent to the dog, he stops within
 * 1.2 m of it (`travel`), and it stands within `DOGS.heelDistance` of the body.
 */
const SHOWN_WITHIN = 4;

/**
 * Check `handlerId` against the world once (`DogState.handlerSpawn`): the man standing where the handler's spawn
 * put him. A handler who has not woken yet (`dormantUntil`) is looked for again next tick; until then the dog is
 * on its own, and names nobody.
 */
function bindHandler(ctx: Ctx, e: Enemy, d: DogState): void {
  const index = d.handlerSpawn;
  if (index === undefined) return;
  const spawn = ctx.statics.def.spawns[index];
  const man = spawn && ctx.world.enemies.find((o) => o.id !== e.id && o.kind === spawn.kind
    && o.homeX === spawn.x && o.homeZ === spawn.z);
  if (man) e.handlerId = man.id;
  else delete e.handlerId;
  if (man || !spawn) delete d.handlerSpawn;
}

function handlerOf(w: World, e: Enemy, d: DogState): Enemy | null {
  if (e.handlerId === undefined || d.handlerSpawn !== undefined) return null;
  return w.enemies.find((o) => o.id === e.handlerId) ?? null;
}

/** Looking for the ranger, as a man sent to a noise or searching is. */
const hunting = (h: Enemy): boolean => h.state === 'investigate' || h.state === 'search';

/**
 * A bark: the `dogBark` event, and a shout's noise out to `DOGS.barkNoise` - which sends whoever hears it to look
 * and raises no alert (alert.ts). Its handler knows its voice and comes wherever he is.
 */
function giveTongue(ctx: Ctx, e: Enemy, d: DogState, handler: Enemy | null): void {
  if (d.barkTicks > 0) return;
  d.barkTicks = secs(DOGS.barkEverySeconds);
  ctx.events.push({ type: 'bark', id: e.id, x: e.x, z: e.z, kind: 'dogBark' });
  makeNoise(ctx, e.x, e.z, DOGS.barkNoise, 'shout', e.id);
  if (handler) sendToInvestigate(handler, e.x, e.z, true);
}

function startCast(e: Enemy, d: DogState, x: number, z: number): void {
  d.mode = 'cast';
  e.targetX = x;
  e.targetZ = z;
  e.stateTicks = secs(DOGS.castSeconds);
  e.hasSpot = false;
}

/** The next point of the sweep, somewhere within `DOGS.castRadius` of where it is casting round. */
function pickSweep(ctx: Ctx, e: Enemy): void {
  const def = ctx.statics.def;
  e.moving = false;
  const a = ctx.rng.range(0, TAU);
  const reach = DOGS.castRadius * (0.3 + 0.7 * ctx.rng.float());
  const x = clamp(e.targetX + sin(a) * reach, 1, def.sizeX - 1);
  const z = clamp(e.targetZ + cos(a) * reach, 1, def.sizeZ - 1);
  e.hasSpot = cellFree(ctx.statics, x, z) && !waterAt(def, x, z);
  e.spotX = x;
  e.spotZ = z;
}

function heel(ctx: Ctx, e: Enemy, d: DogState, handler: Enemy | null): void {
  d.mode = 'heel';
  if (handler && hunting(handler)) {
    startCast(e, d, handler.x, handler.z);
    return;
  }
  const spot = handler ? heelSpot(ctx, handler) : { x: e.homeX, z: e.homeZ, arrive: 0.4 };
  const far = (spot.x - e.x) * (spot.x - e.x) + (spot.z - e.z) * (spot.z - e.z) > DOGS.heelDistance * DOGS.heelDistance;
  if (!go(ctx, e, spot.x, spot.z, far ? DOGS.trackSpeed : DOGS.castSpeed, spot.arrive)) return;
  // There: facing the way its handler faces, or the way its post looks.
  const look = handler ? handler.facing : e.homeFacing;
  standFacing(e, e.x + sin(look), e.z + cos(look));
}

function cast(ctx: Ctx, e: Enemy, d: DogState, handler: Enemy | null): void {
  if (handler && hunting(handler)) {
    e.targetX = handler.x;
    e.targetZ = handler.z;
  }
  if (--e.stateTicks <= 0) {
    heel(ctx, e, d, handler);
    return;
  }
  if (!e.hasSpot || go(ctx, e, e.spotX, e.spotZ, DOGS.castSpeed, 0.5)) pickSweep(ctx, e);
}

/** Heel or cast: a fresh print underfoot sets it running, with one bark to say so. */
function sniff(ctx: Ctx, e: Enemy, d: DogState, handler: Enemy | null): boolean {
  const p = printNear(ctx.world, ctx.statics.def, e.x, e.z, DOGS.pickUpRadius, d.trailTick);
  if (!p) return false;
  d.mode = 'track';
  d.trailTick = p.t;
  giveTongue(ctx, e, d, handler);
  track(ctx, e, d);
  return true;
}

function track(ctx: Ctx, e: Enemy, d: DogState): void {
  const w = ctx.world;
  const trail = w.hazards.tracks;
  // A print reached, it runs straight on for the next in the same tick: no stop at every stride.
  for (let hop = 0; hop < 3; hop++) {
    const i = printIndex(trail, d.trailTick);
    const p = trail[i];
    // The line is broken: he waded (hazards/tracks.ts wipes the back-trail), or it has aged away under its nose.
    if (!p || !freshPrint(p, w.tick)) {
      startCast(e, d, e.x, e.z);
      return;
    }
    if (!go(ctx, e, p.x, p.z, DOGS.trackSpeed, PRINT_REACH)) return;
    const next = trail[i + 1];
    // The newest print, and no scent of him: he is not where his trail ends.
    if (!next) {
      startCast(e, d, p.x, p.z);
      return;
    }
    d.trailTick = next.t;
  }
}

/** It has him: close to `DOGS.bayDistance` and no nearer, face him, and give tongue. */
function bay(ctx: Ctx, e: Enemy, d: DogState, handler: Enemy | null): void {
  const r = ctx.world.ranger;
  d.mode = 'bay';
  e.targetX = r.x;
  e.targetZ = r.z;
  if (go(ctx, e, r.x, r.z, DOGS.trackSpeed, DOGS.bayDistance)) standFacing(e, r.x, r.z);
  giveTongue(ctx, e, d, handler);
}

/** Lost the scent at bay: the newest print near it is the way he went; with none, it casts where it last had him. */
function lostHim(ctx: Ctx, e: Enemy, d: DogState): void {
  const p = printNear(ctx.world, ctx.statics.def, e.x, e.z, DOGS.noseRadius, -1, true);
  if (p) {
    d.mode = 'track';
    d.trailTick = p.t;
  } else startCast(e, d, e.targetX, e.targetZ);
}

/**
 * Whoever its barking brings to its dead handler finds him. Perception finds bodies only for men about their duty
 * (perception.ts), and a man the barks keep sending to look never gets back to his: without this, men stood round a
 * barking dog and a dead man for as long as it barked and raised nothing - which made killing the handler first the
 * safe order, when the design has it the loud one (ROUND8-dogs: "the order is the puzzle").
 */
function showBody(ctx: Ctx, e: Enemy, body: Enemy): void {
  if (body.state !== 'dead' || body.found) return;
  for (const o of ctx.world.enemies) {
    if (o.id === e.id || !isFighter(o) || o.state === 'down' || o.mans !== null) continue;
    if ((o.x - body.x) * (o.x - body.x) + (o.z - body.z) * (o.z - body.z) > SHOWN_WITHIN * SHOWN_WITHIN) continue;
    body.found = true;
    raiseAlert(ctx, 1, body.x, body.z);
    bark(ctx, o, 'manDown');
    return;
  }
}

/** By its handler's body, barking, for as long as it lives. */
function mourn(ctx: Ctx, e: Enemy, d: DogState, handler: Enemy | null): void {
  d.mode = 'mourn';
  if (!handler) e.moving = false;
  else if (go(ctx, e, handler.x, handler.z, DOGS.trackSpeed, DOGS.heelDistance)) standFacing(e, handler.x, handler.z);
  giveTongue(ctx, e, d, null);
  if (handler) showBody(ctx, e, handler);
}

export function stepDog(ctx: Ctx, e: Enemy): void {
  // One hit of anything kills it. `damageEnemy` rolls a man's chance of being left wounded; a dog is not left so.
  if (e.state === 'wounded') {
    e.state = 'dead';
    e.moving = false;
    return;
  }
  e.state = 'down';
  e.detection = 0;
  e.seesRanger = false;
  const d = e.dog ?? (e.dog = freshDogState());
  bindHandler(ctx, e, d);
  if (d.barkTicks > 0) d.barkTicks--;
  const handler = handlerOf(ctx.world, e, d);

  if (d.mode === 'mourn' || (handler !== null && !isFighter(handler))) {
    mourn(ctx, e, d, handler);
    return;
  }
  if (smellsRanger(ctx, e.x, e.z, d.mode === 'bay' ? DOGS.bayDistance + DOGS.bayHold : 0)) {
    bay(ctx, e, d, handler);
    return;
  }
  if (d.mode === 'bay') lostHim(ctx, e, d);
  if (d.mode === 'track') track(ctx, e, d);
  else if (!sniff(ctx, e, d, handler)) {
    if (d.mode === 'cast') cast(ctx, e, d, handler);
    else heel(ctx, e, d, handler);
  }
}
